import { useCallback, useEffect, useRef, useState } from 'react';
import { Box as MuiBox, Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Flex } from '@/layout';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dataUrl: string) => Promise<void> | void;
  onEmptySignature?: () => void;
  title?: string;
}

const SignatureModal = ({
  isOpen,
  onClose,
  onConfirm,
  onEmptySignature,
  title,
}: SignatureModalProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const [hasSignatureStroke, setHasSignatureStroke] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const canvasRatioRef = useRef(1);

  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = window.devicePixelRatio || 1;
    const parentWidth = canvas.parentElement?.clientWidth || Math.min(window.innerWidth - 64, 560);
    const width = Math.min(Math.max(parentWidth - 8, 280), 560);
    const height = 220;
    canvasRatioRef.current = ratio;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f2937';
  }, []);

  const clearCanvasOnly = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = canvasRatioRef.current || 1;
    const width = canvas.width / ratio;
    const height = canvas.height / ratio;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f2937';
  }, []);

  const getCanvasPoint = (event: MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    if ('touches' in event && event.touches[0]) {
      return {
        x: event.touches[0].clientX - rect.left,
        y: event.touches[0].clientY - rect.top,
      };
    }

    if ('changedTouches' in event && event.changedTouches[0]) {
      return {
        x: event.changedTouches[0].clientX - rect.left,
        y: event.changedTouches[0].clientY - rect.top,
      };
    }

    const mouseEvent = event as MouseEvent;
    return {
      x: mouseEvent.clientX - rect.left,
      y: mouseEvent.clientY - rect.top,
    };
  };

  const beginDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    event.preventDefault();
    const point = getCanvasPoint(event.nativeEvent);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    isDrawingRef.current = true;
  };

  const drawSignature = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    event.preventDefault();
    const point = getCanvasPoint(event.nativeEvent);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    setHasSignatureStroke(true);
  };

  const endDrawing = (event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    isDrawingRef.current = false;
  };

  const clearSignature = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setHasSignatureStroke(false);
    clearCanvasOnly();
  };

  const confirmSignature = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas || !hasSignatureStroke) {
      onEmptySignature?.();
      return;
    }

    const dataUrl = canvas.toDataURL('image/png');
    try {
      await onConfirm(dataUrl);
      onClose();
    } catch {
      // Keep the signature modal open so the user can retry after an upload failure.
    }
  };

  useEffect(() => {
    if (isOpen) {
      setHasSignatureStroke(false);
      requestAnimationFrame(() => {
        initializeCanvas();
      });
    }
  }, [isOpen, initializeCanvas]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <MuiBox
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100% - 24px)', sm: 620 },
          bgcolor: '#fff',
          borderRadius: '8px',
          boxShadow: 24,
          p: 3,
        }}
      >
        <Text style={{ display: 'block', marginBottom: '12px', fontWeight: 600 }}>{title || t('pbl.lawyerRegistration.declaration.signatureModalTitle')}</Text>
        <canvas
          ref={canvasRef}
          onMouseDown={beginDrawing}
          onMouseMove={drawSignature}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={beginDrawing}
          onTouchMove={drawSignature}
          onTouchEnd={endDrawing}
          style={{ border: '1px solid #d1d5db', borderRadius: 6, touchAction: 'none', display: 'block', margin: '0 auto' }}
        />
        <Flex justifyContent="flex-end" gridGap={12} marginTop={20}>
          <Button type="button" variant="primary-invert" size="md" onClick={clearSignature}>
            {t('pbl.common.actions.clear')}
          </Button>
          <Button
            type="button"
            variant="primary-invert"
            size="md"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
          >
            {t('pbl.common.actions.cancel')}
          </Button>
          <Button type="button" variant="primary" size="md" onClick={(event) => void confirmSignature(event)}>
            {t('pbl.common.actions.confirmSignature')}
          </Button>
        </Flex>
      </MuiBox>
    </Modal>
  );
};

export default SignatureModal;
